import { Modal } from "@mantine/core";
import type { MutateOptions } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useToast } from "../../../mantine/notifications/useToast";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import type {
  ContractModalMode,
  ContractModalStep,
  DirektvermarktungContract,
  DirektvermarktungContractResponse,
  VermarktungContractTemplateAdjustments
} from "../Direktvermarktung.types";
import { useDirektvermarktungContractModalData } from "../hooks/useDirektvermarktungContractModalData";
import { useDirektvermarktungContractMutations } from "../hooks/useDirektvermarktungContractMutations";
import { DirektvermarktungContractForm } from "./DirektvermarktungContractForm/DirektvermarktungContractForm";
import { VermarktungContractGeneratorsSelection } from "./VermarktungContractGeneratorsSelection";
import { VermarktungContractModalModeSelection } from "./VermarktungContractModalModeSelection";
import { VermarktungContractTemplateAdjustment } from "./VermarktungContractTemplateAdjustment";

interface DirektvermarktungContractModalProps {
  contractId?: string;
  isOpen?: boolean;
  onClose: (contractId?: string) => void;
}

function DirektvermarktungContractModal({
  contractId,
  isOpen,
  onClose
}: DirektvermarktungContractModalProps) {
  const editMode = !!contractId;

  const [newContractId, setNewContractId] = useState<string | undefined>(
    undefined
  );

  function handleClose(contractId?: string) {
    setNewContractId(undefined);
    onClose(contractId);
  }

  return (
    <Modal
      className="DirektvermarktungContractModal"
      opened={isOpen || false}
      size="xl"
      title={`Vertrag ${editMode ? "bearbeiten" : "hinzufügen"}`}
      onClose={() => handleClose(newContractId)}
    >
      {isOpen && (
        <ContractModalSteps
          contractId={contractId}
          newContractId={newContractId}
          onClose={handleClose}
          onCreatedNewContract={setNewContractId}
        />
      )}
    </Modal>
  );
}

function convertFormulaFieldToOldAPI(formula: unknown) {
  if (typeof formula === "undefined" || formula === null) return undefined;
  if (typeof formula === "string") return formula;

  if (formula && typeof formula === "object") {
    return (formula as { formula: string }).formula;
  }
}

interface ContractModalStepsProps {
  contractId?: string;
  newContractId?: string;
  onCreatedNewContract: (contractId: string) => void;
  onClose: (contractId?: string) => void;
}

function ContractModalSteps({
  contractId,
  newContractId,
  onCreatedNewContract,
  onClose
}: ContractModalStepsProps) {
  const editMode = !!contractId;

  const [mode, setMode] = useState<ContractModalMode | undefined>(undefined);
  const [step, setStep] = useState<ContractModalStep>(
    editMode ? "form" : "modeSelection"
  );
  const [templateAdjustments, setTemplateAdjustments] = useState<
    VermarktungContractTemplateAdjustments | undefined
  >(undefined);
  const [responseError, setResponseError] = useState<Error>();

  const { showToast } = useToast();

  const {
    data: modalData,
    isLoading,
    error
  } = useDirektvermarktungContractModalData(
    contractId || templateAdjustments?.copyId
  );

  const {
    direktvermarktungContractCreateMutation,
    direktvermarktungContractEditMutation
  } = useDirektvermarktungContractMutations();
  const isMutating =
    direktvermarktungContractCreateMutation.isPending ||
    direktvermarktungContractEditMutation.isPending;

  const onSubmit: SubmitHandler<DirektvermarktungContract> = (data) => {
    const sanitizedData = {
      ...data,
      contract_remuneration_type: data.contract_remuneration_type ?? null,
      direktvermarkter_id: data.direktvermarkter_id ?? null,
      ppa_fixed_price_components: data.ppa_fixed_price_components?.map(
        (component) => ({
          ...component,
          revenue:
            typeof component.revenue === "number" ? component.revenue : null
        })
      ),
      // TODO in https://node-energy.atlassian.net/browse/WUP-8864: remove after BE integrates new API, for now we map the new editor field back to old structure
      formula_verguetung: convertFormulaFieldToOldAPI(data.formula_verguetung),
      formula_entgelt: convertFormulaFieldToOldAPI(data.formula_entgelt),
      formula_redispatch_entgelt: convertFormulaFieldToOldAPI(
        data.formula_redispatch_entgelt
      ),
      formula_redispatch_verguetung: convertFormulaFieldToOldAPI(
        data.formula_redispatch_verguetung
      ),
      formula_abregelung_entgelt: convertFormulaFieldToOldAPI(
        data.formula_abregelung_entgelt
      ),
      formula_abregelung_verguetung: convertFormulaFieldToOldAPI(
        data.formula_abregelung_verguetung
      )
    } satisfies DirektvermarktungContract;

    const mutationOptions: MutateOptions<
      AxiosResponse<DirektvermarktungContractResponse>,
      Error,
      DirektvermarktungContract
    > = {
      onSuccess: (result) => {
        if (!editMode) {
          showToast({
            type: "success",
            message:
              mode !== "template"
                ? "Vertrag erfolgreich erstellt"
                : "Der Vertrag wurde erfolgreich erstellt. Bitte achten Sie darauf, dass Sie zukünftig startende Verträge im Bereich “Zukünftig” finden."
          });
        }

        if (mode === "template") {
          onCreatedNewContract(result.data.id);
          setStep("generators");
        } else {
          onClose(result?.data?.id);
        }
      },
      onError: (error) => {
        setResponseError(error);
      }
    };

    if (editMode) {
      direktvermarktungContractEditMutation.mutate(
        { ...sanitizedData, id: contractId },
        mutationOptions
      );
    } else {
      direktvermarktungContractCreateMutation.mutate(
        sanitizedData,
        mutationOptions
      );
    }
  };

  function handleModeSelection(selectedMode: ContractModalMode) {
    setMode(selectedMode);
    setStep(selectedMode === "template" ? "templateAdjustment" : "form");
  }

  function handleTemplateAdjustment(
    values: VermarktungContractTemplateAdjustments
  ) {
    setTemplateAdjustments(values);
    setStep("form");
  }

  return (
    <>
      {step === "modeSelection" && (
        <VermarktungContractModalModeSelection
          onCancel={onClose}
          onConfirm={(mode) => handleModeSelection(mode)}
        />
      )}
      {step === "templateAdjustment" && (
        <LoadOrError
          error={error}
          loading={isLoading}
          loadingMessage="Vermarkteroptionen werden geladen ..."
        >
          <VermarktungContractTemplateAdjustment
            onCancel={onClose}
            onConfirm={handleTemplateAdjustment}
          />
        </LoadOrError>
      )}
      {step === "form" && (
        <LoadOrError
          error={error}
          loading={isLoading}
          loadingMessage="Vertrag wird geladen ..."
        >
          <DirektvermarktungContractForm
            direktvermarkter={modalData.direktvermarkter ?? []}
            direktvermarktungContract={
              editMode
                ? modalData.direktvermarktungContract
                : mode === "template"
                  ? {
                      ...modalData.direktvermarktungContract,
                      id: undefined,
                      name: modalData.direktvermarktungContract?.name
                        ? `Kopie von ${modalData.direktvermarktungContract?.name}`
                        : undefined,
                      generation_shares: [],
                      inferred_veraeusserungsform: undefined
                    }
                  : modalData.direktvermarktungContract
            }
            formulaAllowedVariables={
              modalData.formulaAllowedVariables || {
                einspeise_verguetung: [],
                einspeise_entgelt: [],
                redispatch_entgelt: [],
                redispatch_verguetung: [],
                dva_entgelt: [],
                dva_verguetung: []
              }
            }
            formulaTemplates={modalData.formulaTemplates}
            isSubmitting={isMutating}
            operators={modalData.operators ?? []}
            paragraph6Operators={modalData.paragraph6Operators ?? []}
            revenueMonitoringOperators={
              modalData.revenueMonitoringOperators ?? []
            }
            submissionError={responseError}
            templateContract={
              mode === "template"
                ? modalData.direktvermarktungContract
                : undefined
            }
            onCancel={onClose}
            onSubmit={onSubmit}
          />
        </LoadOrError>
      )}
      {step === "generators" && newContractId && (
        <VermarktungContractGeneratorsSelection
          createdContractId={newContractId}
          templateContract={modalData.direktvermarktungContract}
          onClose={() => onClose(newContractId)}
        />
      )}
    </>
  );
}

export { DirektvermarktungContractModal, DirektvermarktungContractModalProps };
